import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
    FiPlus, FiSearch, FiBriefcase, FiUsers, FiTrendingUp,
    FiInfo, FiLayers, FiTarget, FiZap, FiMail, FiGlobe, FiChevronLeft
} from 'react-icons/fi';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import Footer from '../../components/Footer';
import SEO from '../../components/SEO';

const CompanyAbout = () => {
    const navigate = useNavigate();

    return (
        <div className="min-h-screen bg-background flex flex-col">
            <SEO
                title="Recruiters Guide - SnapED codeCampus"
                description="Learn how to maximize your recruitment impact on SnapED. Post jobs, find top talent, and manage applications effortlessly."
            />

            {/* Header Section */}
            <div className="relative overflow-hidden bg-primary/5 py-12 md:py-20">
                <div className="container mx-auto px-4 relative z-10">
                    <Button
                        variant="ghost"
                        size="sm"
                        className="mb-8 hover:bg-primary/10 -ml-2"
                        onClick={() => navigate(-1)}
                    >
                        <FiChevronLeft className="mr-2 h-4 w-4" /> Back
                    </Button>

                    <div className="max-w-3xl">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-xs font-bold text-primary mb-6 uppercase tracking-wider"
                        >
                            <FiInfo className="h-3 w-3" /> Recruiter's Success Hub
                        </motion.div>
                        <motion.h1
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.1 }}
                            className="text-4xl md:text-6xl font-black tracking-tight mb-6"
                        >
                            Hire the Best <span className="text-primary italic">IPU Talent</span>
                        </motion.h1>
                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.2 }}
                            className="text-lg text-muted-foreground leading-relaxed"
                        >
                            SnapED codeCampus bridges the gap between top engineering students and industry-leading companies. Use our guide to navigate the hiring landscape on our platform.
                        </motion.p>
                    </div>
                </div>

                {/* Background Decoration */}
                <div className="absolute top-0 right-0 w-1/3 h-full bg-gradient-to-l from-primary/5 to-transparent -z-1" />
            </div>

            <main className="flex-grow container mx-auto px-4 py-16">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 text-left">
                    <div className="lg:col-span-2 space-y-12 text-left">
                        {/* 1-2-3 Guide */}
                        <section className="space-y-6">
                            <h2 className="text-3xl font-black">Recruiter's Success Guide</h2>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                {[
                                    { step: '1', title: 'Post a Job', desc: 'Create detailed job or internship listings to attract the best talent.', icon: <FiPlus className="h-4 w-4" /> },
                                    { step: '2', title: 'Search Candidates', desc: 'Use our advanced search to find students by branch, skills, and CGPA.', icon: <FiSearch className="h-4 w-4" /> },
                                    { step: '3', title: 'Manage Applications', desc: 'Track and update application statuses from your dedicated dashboard.', icon: <FiLayers className="h-4 w-4" /> }
                                ].map((item, idx) => (
                                    <motion.div
                                        key={idx}
                                        whileHover={{ y: -5 }}
                                        className="relative p-8 rounded-3xl bg-card border group hover:border-primary/50 transition-all shadow-sm"
                                    >
                                        <div className="text-5xl font-black text-primary/10 absolute top-4 right-6 group-hover:text-primary/20 transition-colors uppercase italic">{item.step}</div>
                                        <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center text-primary mb-6 border border-primary/20">{item.icon}</div>
                                        <h4 className="font-black text-lg mb-3">{item.title}</h4>
                                        <p className="text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
                                    </motion.div>
                                ))}
                            </div>
                        </section>

                        {/* Platform Overview */}
                        <section className="space-y-8">
                            <Card className="border-primary/20 bg-gradient-to-br from-card to-secondary/30">
                                <CardHeader>
                                    <CardTitle className="text-2xl font-black">Platform Overview</CardTitle>
                                    <CardDescription>Why hire from SnapED codeCampus?</CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-8">
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                        <div className="p-6 rounded-2xl border bg-background/50 flex gap-4 items-start">
                                            <div className="mt-1 p-3 rounded-xl bg-green-500/10 text-green-500"><FiZap className="h-5 w-5" /></div>
                                            <div>
                                                <div className="font-black text-lg">Verified Talent</div>
                                                <div className="text-sm text-muted-foreground mt-2">Direct access to verified students from top IPU engineering branches.</div>
                                            </div>
                                        </div>
                                        <div className="p-6 rounded-2xl border bg-background/50 flex gap-4 items-start">
                                            <div className="mt-1 p-3 rounded-xl bg-blue-500/10 text-blue-500"><FiTrendingUp className="h-5 w-5" /></div>
                                            <div>
                                                <div className="font-black text-lg">Real-time Analytics</div>
                                                <div className="text-sm text-muted-foreground mt-2">Track application trends and candidate engagement in real-time.</div>
                                            </div>
                                        </div>
                                    </div>
                                    <p className="text-muted-foreground leading-relaxed">
                                        SnapED is more than just a job board; it's an ecosystem built to bridge the gap between academia and industry. By being part of our recruiter network, you get exclusive access to thousands of students actively learning, building, and contributing.
                                    </p>
                                </CardContent>
                            </Card>
                        </section>
                    </div>

                    <aside className="space-y-8 text-left">
                        <Card className="border-primary/20 shadow-lg">
                            <CardHeader>
                                <CardTitle className="text-xl font-black">Need Help?</CardTitle>
                                <CardDescription>We're here to assist you.</CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <Button className="w-full justify-start gap-3 h-14 text-base font-bold" onClick={() => navigate('/contactus')}>
                                    <FiMail className="h-5 w-5" /> Contact Support
                                </Button>
                                <Button variant="outline" className="w-full justify-start gap-3 h-14 text-base font-bold" onClick={() => window.open('/about', '_blank')}>
                                    <FiGlobe className="h-5 w-5" /> Study Hub (Student)
                                </Button>
                                <div className="p-6 rounded-2xl bg-primary/5 border border-primary/10 mt-6">
                                    <p className="text-[10px] uppercase font-black tracking-widest text-primary mb-3">Pro Tip</p>
                                    <p className="text-sm text-muted-foreground leading-relaxed font-medium">
                                        Looking for specialized talent? User the "Talent Search" section in your Job Hub to filter candidates by specific skills like React, Python, or Web3.
                                    </p>
                                </div>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader>
                                <CardTitle className="text-xl font-black text-primary">Reputation System</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-5">
                                <div className="flex justify-between items-center py-3 border-b">
                                    <span className="text-muted-foreground font-medium">Post a Job</span>
                                    <span className="font-black text-green-500">+50 Points</span>
                                </div>
                                <div className="flex justify-between items-center py-3 border-b">
                                    <span className="text-muted-foreground font-medium">Send an Offer</span>
                                    <span className="font-black text-green-500">+10 Points</span>
                                </div>
                                <div className="flex justify-between items-center py-3 border-b">
                                    <span className="text-muted-foreground font-medium">Daily Login</span>
                                    <span className="font-black text-green-500">+5 Points</span>
                                </div>
                                <p className="text-xs text-muted-foreground italic mt-4 leading-relaxed">
                                    Build your company brand and trust among students by staying active and providing high-quality opportunities.
                                </p>
                            </CardContent>
                        </Card>
                    </aside>
                </div>
            </main>

            <Footer />
        </div>
    );
};

export default CompanyAbout;
